/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import { IconSyncBlock } from '@atlaskit/editor-common/assets';
import { messages as blockTypeMessages } from '@atlaskit/editor-common/messages/block-type';
import type {
	QuickInsertActionInsert,
	QuickInsertItem,
} from '@atlaskit/editor-common/provider-factory/quick-insert-provider';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { SyncBlockStoreManager } from '@atlaskit/editor-synced-block-provider/syncBlockStoreManager';
import Lozenge from '@atlaskit/lozenge/new/lozenge';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { createSyncedBlock } from '../editor-commands';
import type { SyncedBlockPlugin } from '../syncedBlockPluginType';
import { SYNCED_BLOCK_BUTTON_TEST_ID } from '../types';

const lozengeWrapperStyles = css({
	flexShrink: 0,
	flexGrow: 0,
	display: 'inline-flex',
});

export const getQuickInsertConfig = (
	config: { enableSourceCreation?: boolean } | undefined,
	api: ExtractInjectionAPI<SyncedBlockPlugin> | undefined,
	syncBlockStore: SyncBlockStoreManager,
): (({
	formatMessage,
}: {
	formatMessage: (message: { defaultMessage: string; id: string }) => string;
}) => QuickInsertItem[]) => {
	return ({ formatMessage }) => {
		if (!config?.enableSourceCreation) {
			return [];
		}

		return [
			{
				id: 'syncBlock',
				title: formatMessage(blockTypeMessages.syncedBlockQuickInsertTitle),
				description: formatMessage(blockTypeMessages.syncedBlockDescription),
				priority: 400,
				keywords: [
					'synced',
					'block',
					'synced-block',
					'sync',
					'sync-block',
					'auto',
					'update',
					'excerpt',
					'connect',
					'create',
				],
				isDisabledOffline: true,
				keyshortcut: '',
				lozenge: !isExperimentEnabled('platform_editor_synced_block_remove_new_tag') ? (
					<span css={lozengeWrapperStyles}>
						<Lozenge
							appearance={
								fg('confluence_fronend_labels_categorization_migration') ? 'discovery' : 'new'
							}
						>
							{formatMessage(blockTypeMessages.newLozenge)}
						</Lozenge>
					</span>
				) : undefined,
				icon: () => (
					<IconSyncBlock label={formatMessage(blockTypeMessages.syncedBlockQuickInsertTitle)} />
				),
				action: (insert: QuickInsertActionInsert, state: EditorState) => {
					return createSyncedBlock({
						tr: state.tr,
						syncBlockStore,
						typeAheadInsert: insert,
						fireAnalyticsEvent: api?.analytics?.actions.fireAnalyticsEvent,
						inputMethod: INPUT_METHOD.QUICK_INSERT,
					});
				},
				testId: SYNCED_BLOCK_BUTTON_TEST_ID.quickInsertCreate,
			},
		];
	};
};
